/**
 * Get API Access Token
 *
 * Token acquisition helper for RTK Query API services.
 * Supports both MSAL and Kinde authentication providers.
 *
 * This module exists separately from useAuth.js to avoid dynamic imports
 * which can cause issues with Vitest mocks in test environments.
 *
 * @module getApiAccessToken
 */

import { AUTH_PROVIDERS, getAuthProvider } from '@/common/helpers/auth/authProviderDetection'
import { getAccessToken } from '@/common/helpers/auth/getAccessToken'
import { isPublicPath } from '@/common/helpers/auth/isPublicPath'

/**
 * Triggers session-expiry handling (redirect to re-authenticate) unless the
 * current route is a public one. Public routes (e.g. `/shared/v2/...`) render
 * without an authenticated session by design; redirecting users away from them
 * would break customer-facing flows (QM-3308).
 *
 * When skipped, the caller receives `{ token: null }` and the request proceeds
 * unauthenticated. Public endpoints succeed; endpoints that require auth will
 * return 401, which `baseQueryWithReauth` handles by redirecting to `/signout`.
 */
const handleSessionExpiryUnlessPublic = async () => {
    if (typeof window !== 'undefined' && isPublicPath(window.location.pathname)) {
        return
    }
    const { handleSessionExpiry } = await import('@/common/helpers/auth/sessionExpiryHandler')
    handleSessionExpiry()
}

/**
 * Get access token for API requests.
 *
 * Detects the current auth provider and acquires the appropriate token:
 * - MSAL (B2C): Uses getAccessToken from getAccessToken.js
 * - Kinde: Uses getRawToken from @kinde-oss/kinde-auth-react/utils
 *
 * @returns {Promise<{token: string|null}>} Access token
 */
export const getApiAccessToken = async () => {
    const provider = getAuthProvider()

    if (provider === AUTH_PROVIDERS.KINDE) {
        try {
            // Dynamic import only for Kinde - MSAL is the default during migration
            const { getRawToken } = await import('@kinde-oss/kinde-auth-react/utils')
            const token = await getRawToken('accessToken')

            // Kinde returns null when session is invalid
            if (!token) {
                console.warn('[getApiAccessToken] Kinde token is null, session expired')
                await handleSessionExpiryUnlessPublic()
                return { token: null }
            }

            return { token }
        } catch (error) {
            console.error('[getApiAccessToken] Failed to get Kinde token:', error)
            await handleSessionExpiryUnlessPublic()
            return { token: null }
        }
    }

    // Default: MSAL (B2C)
    return getAccessToken()
}
